import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, catchError, of } from 'rxjs';

/**
 * Detects new app deployments and forces the browser to load the latest version.
 *
 * How it works:
 *   1. At build time, configure-env.js generates /version.json with a unique hash:
 *      { "version": "a1b2c3d4e5f6", "buildTime": "2026-02-25T15:00:00.000Z" }
 *
 *   2. On app startup, this service fetches /version.json and stores the hash
 *      (e.g. "a1b2c3d4e5f6") as the currentVersion.
 *
 *   3. It then checks for a new version:
 *      - Every 5 minutes via polling
 *      - When the user returns to the tab (visibilitychange event)
 *
 *   4. If the server returns a different hash (new deploy happened), the service
 *      navigates to the same URL with a cache-busting query param:
 *        Before: https://redirect.lifespeak.com/
 *        After:  https://redirect.lifespeak.com/?_v=b2c3d4e5f6a1
 *      This forces the browser to fetch a fresh index.html (bypassing disk cache),
 *      which in turn loads the new hashed JS/CSS bundles.
 *
 * Why not window.location.reload()?
 *   Some browsers serve a cached index.html on reload. Changing the URL itself
 *   guarantees the browser treats it as a new navigation request.
 */
@Injectable({ providedIn: 'root' })
export class VersionService {
  private readonly http = inject(HttpClient);
  private currentVersion: string | null = null;
  private readonly CHECK_INTERVAL_MS = 5 * 60 * 1000;
  private readonly boundVisibilityHandler = this.onVisibilityChange.bind(this);

  initialize(): void {
    // Store the current build version on startup
    this.fetchVersion().subscribe(version => {
      this.currentVersion = version;
    });

    // Poll for new versions every 5 minutes
    setInterval(() => this.checkForUpdate(), this.CHECK_INTERVAL_MS);

    // Also check when user switches back to this tab
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.boundVisibilityHandler);
    }
  }

  private onVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      this.checkForUpdate();
    }
  }

  private checkForUpdate(): void {
    if (!this.currentVersion) {
      return;
    }

    this.fetchVersion().subscribe(latestVersion => {
      if (latestVersion && latestVersion !== this.currentVersion) {
        // New deploy detected — force browser to load fresh index.html
        // e.g. /?_v=a1b2c3 → /?_v=d4e5f6 (different URL = no disk cache hit)
        const url = new URL(window.location.href);
        url.searchParams.set('_v', latestVersion);
        window.location.href = url.toString();
      }
    });
  }

  // Appends ?t={timestamp} to bypass any intermediate caches (CDN, proxy)
  private fetchVersion() {
    const cacheBuster = `?t=${Date.now()}`;
    return this.http.get<{ version: string }>(`/version.json${cacheBuster}`).pipe(
      map(response => response.version),
      catchError(() => of(null)),
    );
  }
}
